import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, DatePicker, Modal, Space, Table, Tag, message } from "antd";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import driversApi from "../../api/drivers";
import orderApi from "../../api/order";
import IntlMessages from "../../components/IntlMessage";
import { formatWallClockDate, formatInstantEastern } from "../../helpers/wallClockDate";
import { truckSize } from "./data";
import Loads from "./Loads";
moment.tz.setDefault("US/Eastern");

const DriverModal = ({ order, intl }) => {
  const t = (id) => intl.formatMessage({ id });
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [driver, setDriver] = useState();
  const [reloadKey, setReloadKey] = useState(0);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const result = await driversApi.getDriverByOrderId(order.id);
      if (result) setDrivers(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, reloadKey]);

  const openInvoices = (record) => {
    setInvoiceModal(true);
    setDriver(record);
  };
  const closeInvoice = () => {
    setInvoiceModal(false);
  };

  const getErrorMessage = (error) => {
    // api interceptor rejects with error.response (not the AxiosError)
    const raw =
      error?.data?.message ||
      error?.response?.data?.message ||
      error?.message ||
      "Request failed";
    return Array.isArray(raw) ? raw.join(", ") : String(raw);
  };

  const confirmClockIn = (record) => {
    let selectedTime = moment();
    Modal.confirm({
      title: t("global.admin_clock_in"),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            {t("global.admin_clock_in_confirm")}
          </p>
          <p style={{ marginBottom: 8 }}>
            {record.driver
              ? `${record.driver.firstname} ${record.driver.lastname}`
              : ""}
          </p>
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            defaultValue={selectedTime}
            style={{ width: "100%" }}
            onChange={(value) => {
              selectedTime = value;
            }}
          />
        </div>
      ),
      okText: t("global.yes"),
      cancelText: t("global.no"),
      onOk: async () => {
        setActionLoading(true);
        try {
          await orderApi.adminClockIn({
            order_id: order.id,
            driver_id: record.driver?.id || record.driver_id,
            start_time: (selectedTime || moment()).format(
              "YYYY-MM-DD HH:mm:ss"
            ),
          });
          message.success("Clocked in successfully");
          setReloadKey((k) => k + 1);
        } catch (error) {
          message.error(getErrorMessage(error));
          throw error;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const confirmClockOut = (record) => {
    let selectedTime = moment();
    Modal.confirm({
      title: t("global.admin_clock_out"),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            {t("global.admin_clock_out_confirm")}
          </p>
          <p style={{ marginBottom: 8 }}>
            {record.driver
              ? `${record.driver.firstname} ${record.driver.lastname}`
              : ""}
          </p>
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            defaultValue={selectedTime}
            style={{ width: "100%" }}
            onChange={(value) => {
              selectedTime = value;
            }}
          />
        </div>
      ),
      okText: t("global.yes"),
      cancelText: t("global.no"),
      onOk: async () => {
        setActionLoading(true);
        try {
          await orderApi.adminClockOut({
            order_id: Number(order.id),
            driver_id: [Number(record.driver?.id || record.driver_id)].filter(
              Boolean
            ),
            // Send the Eastern wall clock shown in the picker, like clock-in.
            // toISOString() sent the UTC instant, which was stored 4-5h ahead
            // on tickets, invoices and job history.
            end_at: (selectedTime || moment()).format("YYYY-MM-DD HH:mm:ss"),
            foreman_name: "Site Admin",
            comment: "Clocked out by site administrator",
          });
          message.success("Clocked out successfully");
          setReloadKey((k) => k + 1);
        } catch (error) {
          message.error(getErrorMessage(error));
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: <IntlMessages id="global.id" />,
      key: "id",
      dataIndex: "id",
      render: (text, record) => {
        return record.id;
      },
    },
    {
      title: <IntlMessages id="global.fullname" />,
      key: "driver",
      dataIndex: "driver",
      render: (text, record) => {
        return (
          <>
            <p>
              {record.driver
                ? `${record.driver.firstname} ${record.driver.lastname}`
                : null}
            </p>
            <p>{record.operator ? record.operator.title : null}</p>
          </>
        );
      },
    },
    {
      title: <IntlMessages id="global.truck" />,
      key: "car",
      dataIndex: "car",
      render: (text, record) => {
        return record.car ? truckSize[record.car.capacity] : null;
      },
    },
    {
      title: <IntlMessages id="invite.invite_status" />,
      key: "invite_status",

      dataIndex: "invite_status",
      render: (text, record) => {
        return record.invite_status === "pending" ? (
          <Tag color="orange">{record.invite_status}</Tag>
        ) : record.invite_status === "accepted" ? (
          <Tag color="success">{record.invite_status}</Tag>
        ) : (
          <Tag color="error">{record.invite_status}</Tag>
        );
      },
    },
    {
      title: <IntlMessages id="global.driver_status" />,
      key: "driver_status",

      dataIndex: "driver_status",
      render: (text, record) => {
        return record.driver_status === "pending" ? (
          <Tag color="orange">{record.driver_status}</Tag>
        ) : record.driver_status === "working" ? (
          <Tag color="purple">{record.driver_status}</Tag>
        ) : record.driver_status === "paused" ? (
          <Tag color="gold">{record.driver_status}</Tag>
        ) : (
          <Tag color="success">{record.driver_status}</Tag>
        );
      },
    },
    {
      title: <IntlMessages id="global.work_on" />,
      key: "work_on",

      dataIndex: "work_on",
      render: (text, record) => {
        return formatWallClockDate(record.work_on);
      },
    },
    {
      title: <IntlMessages id="global.accepted_time" />,
      key: "accepted_at",

      dataIndex: "accepted_at",
      render: (text, record) => {
        // Real accept timestamp — not updated_at (bumped on clock-out).
        const value = record.accepted_at || record.updated_at;
        return value ? formatInstantEastern(value) : null;
      },
    },
    {
      title: <IntlMessages id="global.start_at" />,
      key: "start_at",

      dataIndex: "start_at",
      render: (text, record) => {
        return record.start_at
          ? formatWallClockDate(record.start_at)
          : null;
      },
    },
    {
      title: <IntlMessages id="global.end_at" />,
      key: "end_at",

      dataIndex: "end_at",
      render: (text, record) => {
        return record.end_at
          ? formatWallClockDate(record.end_at)
          : null;
      },
    },
    {
      title: <IntlMessages id="global.loads" />,
      key: "mobile",
      dataIndex: "mobile",
      render: (text, record) => {
        return (
          <Button
            disabled={!(record.loadcount > 0)}
            type="primary"
            size="small"
            onClick={() => openInvoices(record)}
            danger
            shape="round"
          >
            {record.loadcount > 0
              ? `${record.loadcount} (${record.loadsum} tons)`
              : `${record.loadcount}`}
          </Button>
        );
      },
    },
    {
      title: <IntlMessages id="global.action" />,
      key: "action",
      fixed: "right",
      render: (text, record) => {
        const canClockIn =
          record.invite_status === "accepted" &&
          record.driver_status === "pending";
        const canClockOut =
          record.driver_status === "working" ||
          record.driver_status === "paused";

        if (!canClockIn && !canClockOut) {
          return null;
        }

        return (
          <Space size="small">
            {canClockIn ? (
              <Button
                type="primary"
                size="small"
                loading={actionLoading}
                onClick={() => confirmClockIn(record)}
              >
                <IntlMessages id="global.admin_clock_in" />
              </Button>
            ) : null}
            {canClockOut ? (
              <Button
                type="primary"
                danger
                size="small"
                loading={actionLoading}
                onClick={() => confirmClockOut(record)}
              >
                <IntlMessages id="global.admin_clock_out" />
              </Button>
            ) : null}
          </Space>
        );
      },
    },
  ];
  return (
    <>
      <Table
        tableLayout="auto"
        columns={columns}
        dataSource={drivers}
        rowKey="id"
        size="small"
        loading={loading}
        scroll={{
          x: "max-content",
        }}
        pagination={null}
      />
      {invoiceModal ? (
        <Loads
          orderId={order.id}
          driver={driver}
          open={invoiceModal}
          onClose={closeInvoice}
        />
      ) : null}
    </>
  );
};
export default injectIntl(DriverModal);
